import { lruMemoize } from '@reduxjs/toolkit';
import { isNil } from 'lodash-es';

import type {
  AssetsAssetTranscriptionPropertiesSchemaWithIdNullable,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';

import type { AllWordsIndex, PrefixTreeNode, SpeakerPart, Word } from './types';

type GetTranscriptionPartsAndSpeakerLabelsType = {
  transcriptionSegments: AssetsSegmentSchemaWithId[];
  transcriptionProperties:
    | AssetsAssetTranscriptionPropertiesSchemaWithIdNullable
    | undefined;
};

export const getTranscriptionPartsAndSpeakerLabels = ({
  transcriptionSegments,
  transcriptionProperties,
}: GetTranscriptionPartsAndSpeakerLabelsType) => {
  const segmentsSorted = transcriptionSegments.toSorted((a, b) => {
    if (a.time_start_milliseconds && b.time_start_milliseconds) {
      return a.time_start_milliseconds - b.time_start_milliseconds;
    }
    return 0;
  });

  const speakerParts: SpeakerPart[] = [];

  segmentsSorted.forEach(({ id: segmentId, transcription }) => {
    if (!segmentId || !transcription) {
      return;
    }
    const lastGroup = speakerParts[speakerParts.length - 1];
    // Create new group if groups are empty or if the speaker has changed
    if (!speakerParts.length || lastGroup.speaker !== transcription.speaker) {
      const newPart: SpeakerPart = {
        speaker: transcription.speaker,
        ids: [segmentId],
      };
      speakerParts.push(newPart);
    } else {
      lastGroup.ids.push(segmentId);
    }
  });

  const speakerLabelsFromProperties =
    transcriptionProperties?.speaker_labels ?? {};
  const speakerLabels: Record<string, string> = {
    ...speakerLabelsFromProperties,
  };
  speakerParts
    .map((part) => part.speaker)
    .forEach((speakerId) => {
      if (typeof speakerId === 'number' && !speakerLabels[speakerId]) {
        speakerLabels[speakerId] = `Speaker ${speakerId + 1}`;
      }
    });

  return { speakerParts, speakerLabels };
};

export const getSpeakerPartBySegmentIdMap = lruMemoize(
  (speakerParts: SpeakerPart[]) => {
    const speakerMap = new Map<string, number>();
    for (const speakerPart of speakerParts) {
      for (const segmentId of speakerPart.ids) {
        if (isNil(speakerPart.speaker)) {
          continue;
        }
        speakerMap.set(segmentId, speakerPart.speaker);
      }
    }
    return speakerMap;
  },
);

export const getSelectAllWords = (
  transcriptionSegments: AssetsSegmentSchemaWithId[],
) => {
  let allWords: Word[] = [];
  let globalIndex = -1;

  transcriptionSegments.forEach((segment, segmentIndex) => {
    if (segment && segment.transcription && segment.transcription.words) {
      allWords = allWords.concat(
        segment.transcription.words.map((word, index) => {
          globalIndex += 1;

          return {
            segmentId: segment.id,
            segmentIndex,
            localIndex: index,
            globalIndex,
            timeStart:
              !isNil(segment.time_start_milliseconds) &&
              word.start_ms !== undefined
                ? segment.time_start_milliseconds + word.start_ms
                : undefined,
            timeEnd:
              !isNil(segment.time_start_milliseconds) &&
              word.end_ms !== undefined
                ? segment.time_start_milliseconds + word.end_ms
                : undefined,
            ...word,
          };
        }),
      );
    }
  });

  const allWordsIndex: AllWordsIndex = {};
  for (let i = 0; i < allWords.length; i += 1) {
    const id = `w_${allWords[i].segmentId}_${allWords[i].localIndex}`;
    allWordsIndex[id] = i;
  }

  return { allWords, allWordsIndex };
};

export const getSelectWordsIndexTree = (allWords: Word[]) => {
  const tree: PrefixTreeNode = { children: {} };
  allWords.forEach((word) => {
    let curPointer = tree;
    const wordId = `w_${word.segmentId}_${word.localIndex}`;

    const stringToParse = word.value.toLowerCase();

    for (let charIndex = 0; charIndex < stringToParse.length; charIndex += 1) {
      const char = stringToParse.charAt(charIndex);
      let child = curPointer.children[char];
      if (!child) {
        child = { children: {} };
        curPointer.children[char] = child;
      }

      if (charIndex === stringToParse.length - 1) {
        child.values ??= [];
        if (!child.values.includes(wordId)) {
          child.values.push(wordId);
        }
      }

      curPointer = child;
    }
  });
  return tree;
};
